// Right-click menu for the trend overlay graph: Y-axis range + per-sensor colors.

import React from 'react';
import PropTypes from 'prop-types';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

const ChartContextMenu = ({ open, position, onClose, axisSettings, onApplyAxis, sensors, onColorChange, unitLabel, t }) => {
  const [auto, setAuto] = React.useState(true);
  const [min, setMin] = React.useState('');
  const [max, setMax] = React.useState('');
  const paperRef = React.useRef(null);

  // Re-seed the draft inputs from the applied settings each time the menu opens.
  React.useEffect(() => {
    if (open) {
      setAuto(axisSettings.auto !== false);
      setMin(axisSettings.min != null ? String(axisSettings.min) : '');
      setMax(axisSettings.max != null ? String(axisSettings.max) : '');
    }
  }, [open]);

  // DraggableAppModal's global stylesheet hides every MUI backdrop while a
  // draggable modal is on screen (body:has(.draggable-modal-override)
  // .MuiBackdrop-root { display: none }), so MUI's own backdrop-click close
  // never fires. Close on any mousedown outside the menu paper instead.
  React.useEffect(() => {
    if (!open) return undefined;
    const handleOutsideMouseDown = (e) => {
      if (paperRef.current && !paperRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleOutsideMouseDown, true);
    return () => document.removeEventListener('mousedown', handleOutsideMouseDown, true);
  }, [open, onClose]);

  const minNum = parseFloat(min);
  const maxNum = parseFloat(max);
  const applyDisabled = !auto && (isNaN(minNum) || isNaN(maxNum) || minNum >= maxNum);

  const handleApply = () => {
    onApplyAxis(auto ? { auto: true } : { auto: false, min: minNum, max: maxNum });
    onClose();
  };

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position ? { top: position.y, left: position.x } : undefined}
      PaperProps={{ ref: paperRef }}
    >
      <ListSubheader sx={{ lineHeight: '32px' }}>{`${t('axis_settings')} (${unitLabel})`}</ListSubheader>
      <MenuItem
        disableRipple
        onKeyDown={(e) => e.stopPropagation()}
        sx={{ display: 'flex', gap: 1, alignItems: 'center', '&:hover': { background: 'transparent' }, cursor: 'default' }}
      >
        <FormControlLabel
          control={<Checkbox size="small" checked={auto} onChange={(e) => setAuto(e.target.checked)} />}
          label={t('auto')}
        />
        <TextField
          label={t('min')}
          size="small"
          type="number"
          disabled={auto}
          value={min}
          onChange={(e) => setMin(e.target.value)}
          sx={{ width: 90 }}
        />
        <TextField
          label={t('max')}
          size="small"
          type="number"
          disabled={auto}
          value={max}
          onChange={(e) => setMax(e.target.value)}
          sx={{ width: 90 }}
        />
        <Button size="small" variant="contained" disabled={applyDisabled} onClick={handleApply}>
          {t('apply')}
        </Button>
      </MenuItem>
      <Divider />
      <ListSubheader sx={{ lineHeight: '32px' }}>{t('sensor_colors')}</ListSubheader>
      {(sensors || []).map((s) => (
        <MenuItem
          key={s.sensorId}
          disableRipple
          sx={{ '&:hover': { background: 'transparent' }, cursor: 'default' }}
        >
          <input
            type="color"
            value={s.color}
            onChange={(e) => onColorChange(s.sensorId, e.target.value)}
            style={{ marginRight: 8, width: 28, height: 20, border: 'none', padding: 0, cursor: 'pointer' }}
          />
          {s.label}
        </MenuItem>
      ))}
    </Menu>
  );
};

ChartContextMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  position: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  onClose: PropTypes.func.isRequired,
  axisSettings: PropTypes.object.isRequired,
  onApplyAxis: PropTypes.func.isRequired,
  sensors: PropTypes.array.isRequired,
  onColorChange: PropTypes.func.isRequired,
  unitLabel: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default ChartContextMenu;
